<script lang="ts">
  /**
   * Les Mains de Dieu.
   *
   * Le livre en donne sept **en exemple**, et dit surtout comment on en
   * fabrique une : la Compétence Main du Démon, le Plan, les Pièces, des Perles
   * de Veredium et des Matériels d'amélioration, en quantité dictée par le
   * Niveau de Veredium. Rien n'oblige donc à s'en tenir aux sept — une table qui
   * en dessine une n'a pas à sortir de l'application pour l'y inscrire.
   *
   * Une Main de Dieu est le seul cas du jeu où la Compétence testée appartient à
   * l'**arme** : la Catégorie « Mains de Dieu » n'en nomme aucune, parce que
   * chacune nomme la sienne. C'est pourquoi le formulaire la demande.
   */
  import {
    mainsDeDieu as lireLesMains,
    type Fiche,
    type PersonnageEnregistre,
  } from '../../../rules'

  let {
    vue,
    personnage,
    forger,
    acheter,
    dessiner,
    baptiser,
    charger,
    defaire,
  }: {
    vue: Fiche
    personnage: PersonnageEnregistre
    /** Forge une Main du livre, sur son Plan. Le matériel part, réussi ou non. */
    forger: (modele: string, reussi: boolean) => void
    /** Achète une Main toute faite, au prix du livre en Points d'Influence. */
    acheter: (modele: string) => void
    /** Inscrit une Main que la table a dessinée — au même prix que celles du livre. */
    dessiner: (main: {
      nom: string
      niveau: number
      competence: string
      degats: number | null
      plusForceDeFrappe: boolean
      portee: string | null
      special: string | null
      tribut: string | null
    }, reussi: boolean) => void
    baptiser: (index: number, nom: string) => void
    /** Charge l'exemplaire, ou en dépense une munition. */
    charger: (index: number, delta: number) => void
    defaire: (index: number) => void
  } = $props()

  const armurerie = $derived(lireLesMains(personnage))
  /** `plans` : les sept du livre. `atelier` : celle qu'on dessine. */
  let ouvert = $state<'ferme' | 'plans' | 'atelier'>('ferme')

  /* Ce que la table dessine. Rien n'est deviné : tout vient d'elle. */
  let nom = $state('')
  let niveau = $state(1)
  let competence = $state('tir_lancer')
  let degats = $state<number | null>(null)
  let avecFf = $state(false)
  let portee = $state('')
  let special = $state('')
  let tribut = $state('')

  /* Les Compétences que le livre associe à ses sept Mains — Tir/Lancer pour
     six, Corps-à-corps pour le Passe-murailles. Rien n'interdit une autre :
     c'est la table qui tranche. */
  const competences = $derived(vue.competences.map((c) => ({ id: c.id, nom: c.nom })))

  /** Le Niveau choisi dans l'atelier, confronté au sac. */
  const niveauChoisi = $derived(armurerie.niveaux.find((n) => n.niveau === niveau))

  function inscrire(reussi: boolean) {
    if (nom.trim().length === 0) return
    dessiner(
      {
        nom,
        niveau,
        competence,
        degats,
        plusForceDeFrappe: avecFf,
        portee: portee.trim() || null,
        special: special.trim() || null,
        tribut: tribut.trim() || null,
      },
      reussi,
    )
    // Raté, le matériel est parti quand même : on garde le brouillon pour
    // retenter sans tout ressaisir.
    if (!reussi) return
    nom = ''
    degats = null
    avecFf = false
    portee = ''
    special = ''
    tribut = ''
    ouvert = 'ferme'
  }
</script>

<div class="cartouche">
  <div class="bandeau">
    Mains de Dieu
    {#if ouvert !== 'ferme'}
      <button type="button" class="refermer" onclick={() => (ouvert = 'ferme')}>Refermer</button>
    {/if}
  </div>
  <div class="corps">
    {#each armurerie.forgees as m (m.index)}
      <section class="main">
        <h3>
          <input
            class="saisie nom-main"
            type="text"
            value={m.nom}
            aria-label="Nom de la Main de Dieu"
            onchange={(e) => baptiser(m.index, e.currentTarget.value)}
          />
          <span class="voie">
            Niveau {m.niveau_de_veredium}
            {#if m.modele === null}· dessinée à la table{:else if m.page}· p. {m.page}{/if}
          </span>
          <button type="button" class="lien" onclick={() => defaire(m.index)}>Défaire</button>
        </h3>
        <span class="detail">
          {#if m.degats !== null}
            <strong class="data">{m.degats}{m.plus_force_de_frappe ? ' + FF' : ''}</strong> Dégâts
          {:else}
            Aucun Dégât direct — tout passe par son Spécial.
          {/if}
          {#if m.competence_nom}· {m.competence_nom}{/if}
          {#if m.portee}· portée {m.portee}{/if}
        </span>
        {#if m.special}<span class="detail">{m.special}</span>{/if}
        {#if m.tribut}<span class="detail tribut">Tribut : {m.tribut}</span>{/if}

        <!-- Le magasin. Quatre des sept Mains consomment une munition, et le
             livre chiffre ce qu'elles tiennent. Le prix ne se décompte pas : il
             se paie au lot, en PI ou au marché noir, et la table tranche. Ce
             qui se tient ici, c'est le compte — celui qu'on oublie en séance. -->
        {#if m.munition || m.munitions > 0}
          <span class="magasin">
            <span class="compte data" class:vide={m.munitions === 0}>
              {m.munitions}{#if m.capacite !== null} / {m.capacite}{/if}
            </span>
            {#if m.munition}<span class="detail">{m.munition}</span>{/if}
            <button
              type="button"
              class="pilule discret"
              disabled={m.munitions === 0}
              onclick={() => charger(m.index, -1)}
            >
              Tirer
            </button>
            <button
              type="button"
              class="pilule"
              disabled={m.capacite !== null && m.munitions >= m.capacite}
              onclick={() => charger(m.index, 1)}
            >
              Charger
            </button>
            {#if m.prix_du_lot}
              <span class="detail">
                {m.prix_du_lot}{#if m.prix_faction} · {m.prix_faction} £ l'unité{/if}{#if m.prix_marche} · {m.prix_marche} £ au marché noir{/if}
              </span>
            {/if}
          </span>
        {/if}
      </section>
    {/each}

    {#if ouvert === 'ferme'}
      {#if armurerie.forgees.length === 0}
        <p class="chapo">
          Rien à l'armurerie. Le livre en donne sept, et la règle pour en
          fabriquer d'autres : une Main de Dieu se dessine aussi bien à la
          table.
        </p>
      {/if}
      <p class="regle">
        Forger demande la Compétence Main du Démon, le Plan, les Pièces, des
        Perles de Veredium et des Matériels d'amélioration — le Niveau dit
        combien de chaque, et le Test consomme tout, réussi ou non.
      </p>
      <p class="regle">
        L'autre voie est l'achat : le livre chiffre chaque Main en Points
        d'Influence. Une Main achetée arrive toute faite, sans Perle, sans
        Matériel et sans dé — elle coûte seulement plus d'Influence.
      </p>
      <!-- Chaque exigence, en face de ce qu'on a. C'est ce qui manquait : la
           prose du livre s'affichait sans jamais être comparée au sac, et un
           Kit acheté pour forger n'était compté nulle part. -->
      <ul class="niveaux">
        {#each armurerie.niveaux as n (n.niveau)}
          <li class:atteint={n.forgeable}>
            <strong>Niveau {n.niveau}</strong>
            <span class="compte" class:manque={n.materiels_possedes < n.materiels_requis}>
              {n.materiels_requis} Matériel{n.materiels_requis > 1 ? 's' : ''} d'amélioration
              <span class="data">({n.materiels_possedes} au sac)</span>
            </span>
            <span class="compte" class:manque={n.perles_possedees < n.perles_requises}>
              {n.perles_requises} Perle{n.perles_requises > 1 ? 's' : ''} de Veredium
              <span class="data">({n.perles_possedees} au sac)</span>
            </span>
            {#if n.capacite_requise}
              <span class="compte" class:manque={!n.capacite_acquise}>
                Capacité {n.capacite_requise}
                {#if !n.capacite_acquise}<span class="data">(absente)</span>{/if}
              </span>
            {/if}
            {#if n.test}<span class="compte test">{n.test}</span>{/if}
          </li>
        {/each}
      </ul>
      <div class="gestes">
        <button type="button" class="lien" onclick={() => (ouvert = 'plans')}>
          Forger ou acheter une Main du livre
        </button>
        <button type="button" class="lien" onclick={() => (ouvert = 'atelier')}>
          En dessiner une avec le meneur
        </button>
      </div>
    {:else if ouvert === 'plans'}
      <ul class="achats larges">
        {#each armurerie.modeles as m (m.modele)}
          {@const n = armurerie.niveaux.find((x) => x.niveau === m.niveau_de_veredium)}
          <li>
            <span class="nom">
              {m.nom}
              <span class="exigence">
                Niveau {m.niveau_de_veredium} · {m.competence_nom ?? '—'}
                {#if m.degats !== null}
                  · {m.degats}{m.plus_force_de_frappe ? ' + FF' : ''} Dégâts
                {/if}
                {#if m.portee}· portée {m.portee}{/if}
              </span>
              {#if m.special}<span class="exigence">{m.special}</span>{/if}
              {#if m.tribut}<span class="manque">Tribut : {m.tribut}</span>{/if}
            </span>
            <span class="chiffres data">
              {#if m.prix_pi !== null}{m.prix_pi} PI{/if}
            </span>
            <!-- L'autre voie : le livre chiffre chaque Main, on peut donc
                 l'acheter toute faite. Plus cher en Influence que la forge,
                 mais sans Perle, sans Matériel et sans dé. -->
            {#if m.prix_pi !== null}
              <span class="gestes">
                <button
                  type="button"
                  class="pilule discret"
                  disabled={vue.compteurs.pi_disponibles < m.prix_pi}
                  title={vue.compteurs.pi_disponibles < m.prix_pi
                    ? `Il manque ${m.prix_pi - vue.compteurs.pi_disponibles} PI`
                    : `Achetée toute faite pour ${m.prix_pi} PI`}
                  onclick={() => acheter(m.modele ?? '')}
                >
                  Acheter
                </button>
              </span>
            {/if}
            <!-- Le Test se jette au tiroir ; on rapporte le verdict. Raté, le
                 matériel est parti quand même. Sans le matériel du Niveau, rien
                 ne se propose : on ne forge pas à crédit. -->
            <span class="gestes">
              <button
                type="button"
                class="pilule"
                disabled={!n?.forgeable}
                onclick={() => forger(m.modele ?? '', true)}
              >
                Test réussi
              </button>
              <button
                type="button"
                class="pilule discret"
                disabled={!n?.forgeable}
                onclick={() => forger(m.modele ?? '', false)}
              >
                Raté
              </button>
            </span>
          </li>
        {/each}
      </ul>
    {:else}
      <p class="regle">
        Ce que vous inscrivez ici, c'est ce dont le joueur et le meneur sont
        convenus. L'application ne l'invente pas — elle la traite ensuite comme
        les sept du livre : elle se prend en main et se jette pareil.
      </p>
      <div class="atelier">
        <label class="champ large">
          Nom
          <input class="saisie" type="text" placeholder="Le Rossignol d’Ada" bind:value={nom} />
        </label>
        <label class="champ">
          Niveau
          <select class="saisie" bind:value={niveau}>
            {#each armurerie.niveaux as n (n.niveau)}
              <option value={n.niveau}>Niveau {n.niveau}</option>
            {/each}
          </select>
        </label>
        <label class="champ">
          Compétence
          <select class="saisie" bind:value={competence}>
            {#each competences as c (c.id)}
              <option value={c.id}>{c.nom}</option>
            {/each}
          </select>
        </label>
        <label class="champ">
          Dégâts
          <input
            class="saisie court"
            type="number"
            min="0"
            placeholder="—"
            bind:value={degats}
          />
        </label>
        <label class="champ case">
          <input type="checkbox" bind:checked={avecFf} />
          + Force de frappe
        </label>
        <label class="champ">
          Portée
          <input class="saisie court" type="text" placeholder="Moyenne" bind:value={portee} />
        </label>
        <label class="champ large">
          Spécial
          <input
            class="saisie"
            type="text"
            placeholder="Ce que l'arme fait au-delà de ses Dégâts"
            bind:value={special}
          />
        </label>
        <label class="champ large">
          Tribut
          <input
            class="saisie"
            type="text"
            placeholder="Ce qu'elle coûte, et à quelle condition"
            bind:value={tribut}
          />
        </label>
        <div class="gestes">
          <button
            type="button"
            class="pilule"
            disabled={nom.trim().length === 0 || !niveauChoisi?.forgeable}
            onclick={() => inscrire(true)}
          >
            Test réussi
          </button>
          <button
            type="button"
            class="pilule discret"
            disabled={nom.trim().length === 0 || !niveauChoisi?.forgeable}
            onclick={() => inscrire(false)}
          >
            Raté
          </button>
          {#if niveauChoisi && !niveauChoisi.forgeable}
            <span class="manque">le sac ne suffit pas pour ce Niveau</span>
          {/if}
        </div>
        <p class="regle">
          Le Tribut d'une Main de Dieu ne se déclenche pas sur un 1D100 comme
          celui d'une Amélioration : il a sa propre clause, écrite dans sa fiche.
        </p>
      </div>
    {/if}
  </div>
</div>

<style>
  /* Le magasin d'un exemplaire, sous sa fiche. */
  .magasin {
    display: flex;
    align-items: center;
    gap: var(--pas);
    flex-wrap: wrap;
    margin-top: var(--pas);
  }
  .magasin .compte {
    font-size: 1.1rem;
    font-weight: 600;
  }
  /* À sec : l'arme est là, elle ne tire plus. */
  .magasin .compte.vide {
    color: var(--ko);
  }
  /* Les Niveaux, exigence par exigence, en face du sac. */
  .niveaux {
    list-style: none;
    margin: 0 0 calc(var(--pas) * 2);
    padding: 0;
    display: grid;
    gap: var(--pas);
  }
  .niveaux li {
    display: flex;
    flex-wrap: wrap;
    gap: calc(var(--pas) * 0.5) calc(var(--pas) * 2);
    align-items: baseline;
    font-size: 0.82rem;
    color: var(--ink-soft);
    padding-left: calc(var(--pas) * 1.5);
    border-left: 2px solid var(--line);
  }
  .niveaux li.atteint {
    border-left-color: var(--ok);
    color: var(--ink);
  }
  .niveaux .compte.manque {
    color: var(--ko);
  }
  .niveaux .compte.test {
    font-style: italic;
  }
  /* Une Main forgée : son nom se saisit, le reste se lit. */
  .main {
    display: grid;
    gap: 2px;
    padding-bottom: var(--pas);
    margin-bottom: var(--pas);
    border-bottom: 1px solid var(--trait);
  }
  .main h3 {
    margin: 0;
    display: flex;
    align-items: baseline;
    gap: var(--pas);
    flex-wrap: wrap;
  }
  .nom-main {
    font-family: var(--titre);
    font-size: 0.95rem;
    max-width: 26ch;
  }
  .tribut {
    color: var(--ko);
  }
  /* L'établi : quatre champs courts, trois longs. */
  .atelier {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
    gap: var(--pas);
    align-items: end;
  }
  .atelier .champ {
    display: grid;
    gap: 2px;
    font-size: 0.68rem;
    font-family: var(--titre);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--pale);
  }
  .atelier .champ.large,
  .atelier .gestes,
  .atelier .regle {
    grid-column: 1 / -1;
  }
  .atelier .champ.case {
    display: flex;
    align-items: center;
    gap: calc(var(--pas) / 2);
    text-transform: none;
    letter-spacing: 0;
    font-family: inherit;
    font-size: 0.78rem;
    color: var(--ink);
  }
  .atelier .saisie.court {
    width: 8ch;
  }
  .gestes {
    display: flex;
    gap: calc(var(--pas) * 2);
    flex-wrap: wrap;
  }
  .cartouche {
    margin-bottom: calc(var(--pas) * 3);
  }
  .cartouche .corps {
    padding: calc(var(--pas) * 3);
    gap: calc(var(--pas) * 3);
  }
</style>
